"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronDown, ChevronUp } from "lucide-react";
import { addDays } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { LanguageSelector } from "./LanguageSelector";
import { SheetVariantFour } from "./SheetVariantFour";
import { ModalVariantThree } from "./ModalVariantThree";
import { ShowFilters } from "./ShowFilters";
import { FilterBar } from "./FilterBar";
import { FilterDrawer } from "./FilterDrawer";
import { TimeRangeSelector } from "./TimeRangeSelector";
import { GroupSizeExceedsModal } from "./GroupSizeExceedsModal";
import { BookingGapWarningModal } from "./BookingGapWarningModal";
import { ShowMaxParticipantsModal } from "./ShowMaxParticipantsModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ShowCardVariant5 } from "./ShowCardVariant5";
import { type Show } from "@/lib/mockData";
import { useSearchParams, useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useCalendarAvailability } from "@/hooks/useCalendarAvailability";
import { useVenueDetails } from "@/hooks/useVenueDetails";
import { timeToMinutes, formatTimeDisplay } from "@/lib/utils/time";
import { toast } from "sonner";
import { getCurrencySymbol } from "@/lib/utils/currency";

// Extend Show type with availability
interface ShowWithAvailability extends Show {
  isAvailable?: boolean;
}

// Define the API response type
interface ApiShow {
  id: number; // This is actually the show_time_slot_pricing_id
  show_id: number;
  name: string;
  subtitle: string | null;
  description: string | null;
  time: string;
  day_of_week: number | null;
  duration: string;
  price: number;
  currency: string;
  price_label: string | null;
  vat_percentage: number;
  include_vat: boolean;
  price_point_id: number;
  pricing_id: number | null; // The actual pricing record ID
  min_participants: number;
  max_participants: number;
  min_charge_participants?: number | null;
  cut_off_days?: number;
  media: {
    primary_image: any | null;
    videos: any[];
    images: any[];
    primary_image_url: string | null;
    video_urls: string[];
    image_urls: string[];
  };
  studios: Array<{
    studio_id: number;
    name: string;
    capacity: number;
    status_id: number;
  }>;
  language: string;
  faqs: Array<{
    id: number;
    question: string | null;
    answer: string | null;
  }>;
  venue_info: {
    name: string | null;
    description: string | null;
    defaultLanguage?: string;
  };
  venueDefaultLanguage?: string; // Venue's default language for time formatting
  isAvailable?: boolean; // Availability status from the shows API
}

interface ShowsSectionProps {
  initialGroupSize?: number;
  selectedDate: Date | undefined;
  excludeBookingId?: number;
  onBack: () => void;
  onShowSelect: (show: ShowWithAvailability, date: Date) => void;
  onNavigate?: (url: string, options?: { scroll?: boolean }) => void;
  venueName?: string;
  supportEmail?: string | null;
  supportPhone?: string | null;
}

type ShowVariant = "sheet" | "modal" | "expand";

interface TimeSlotProps {
  show: ShowWithAvailability;
  onShowSelect: (show: ShowWithAvailability) => void;
  onBookNow: (show: ShowWithAvailability) => void;
  variant: ShowVariant;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

// Helper functions
const getGroupSizeNumbers = (groupSize: string) => {
  const match = groupSize.match(/(\d+)-(\d+)/);
  if (match) {
    const min = parseInt(match[1]);
    const max = parseInt(match[2]);
    return { min, max, avg: Math.ceil((min + max) / 2) };
  }
  return { min: 1, max: 1, avg: 1 };
};

// Convert API show to local Show type
const convertApiShowToShow = (apiShow: ApiShow, bookingType?: string): Show => {
  // The API now returns the correctly calculated price
  // For private customers: price already includes VAT
  // For corporate customers: price excludes VAT
  // So we can use the price directly from the API
  const displayPrice = apiShow.price;

  return {
    id: apiShow.show_id,
    name: apiShow.name,
    subtitle: apiShow.subtitle || "",
    description: apiShow.description || "",
    startTime: apiShow.time.substring(0, 5), // Convert "10:00:00" to "10:00"
    duration: apiShow.duration,
    price: displayPrice,
    groupSize: `${apiShow.min_participants}-${apiShow.max_participants}`,
    image: apiShow.media.primary_image_url || "/default-show-image.jpg",
    images: apiShow.media.image_urls || [],
    videos: apiShow.media.video_urls || [],
    status: undefined, // API only returns available shows, undefined means available
    vat: apiShow.vat_percentage,
    currency: apiShow.currency,
    language: apiShow.language,
    priceLabel: apiShow.price_label || undefined,
    faqs: apiShow.faqs || [],
    venue_info: apiShow.venue_info || { name: null, description: null },
    min_participants: apiShow.min_participants,
    max_participants: apiShow.max_participants,
    min_charge_participants: apiShow.min_charge_participants || null,
    cut_off_days: apiShow.cut_off_days, // Add cut_off_days from API response
    has_booking_conflict: false, // API shows are always available
    isAvailable: apiShow.isAvailable, // Map the availability from the API
    // Add pricing-related IDs for booking creation
    pricing_id: apiShow.pricing_id,
    price_point_id: apiShow.price_point_id,
    show_time_slot_pricing_id: (apiShow as any).id, // The id is actually the show_time_slot_pricing_id
    studios: apiShow.studios,
    // Add default values for missing fields
    minimumCharge: apiShow.price * 0.8,
    features: [
      "Interactive experience",
      "Professional game master",
      "Pre-game briefing",
      "Post-game photo opportunity",
    ],
    difficulty: "Medium",
    successRate: "65%",
    minAge: 12,
    emoji: "🎮",
  };
};

// COMPACT ROW OF INFORMATION OF EACH SHOW/EVENT
const ShowCompactRow = ({
  show,
  onBookNow,
  onInfoClick,
  variant,
  isExpanded,
  t,
  bookingType,
  locale,
}: {
  show: ShowWithAvailability;
  onBookNow: () => void;
  onInfoClick: (e: React.MouseEvent) => void;
  variant: ShowVariant;
  isExpanded?: boolean;
  t: any;
  bookingType?: string;
  locale?: string;
}) => {
  const searchParams = useSearchParams();

  // Check if booking is within cut-off period
  const isWithinCutOff = (() => {
    const dateStr = searchParams.get("date");
    if (!dateStr) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const bookingDate = new Date(dateStr);
    bookingDate.setHours(0, 0, 0, 0);

    const cutOffDays = show.cut_off_days ?? 7; // Default to 7 days if not specified
    const daysDifference = Math.floor((bookingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference < cutOffDays;
  })();

  const vatLabel =
    bookingType === "company"
      ? t("booking.shows.card.excludingVat")
      : t("booking.shows.card.includingVat");
  // Use "kr" instead of "SEK" for better display
  const currencyDisplay = show.currency === "SEK" ? "kr" : getCurrencySymbol(show.currency || "SEK");
  const priceText = `${show.price} ${currencyDisplay}`;
  // Remove per person label for desktop
  const perPersonLabel = "";

  return (
    <div className="flex items-center gap-4">
      <div className="w-28 flex-shrink-0 self-start text-left space-y-1">
        <div className="font-semibold text-base text-left">
          🕐 {formatTimeDisplay(show.startTime, locale)}
        </div>
        <div className="font-semibold text-base">💰 {priceText}</div>
      </div>

      <div className="flex-1 min-w-0 self-start">
        <h3 className="font-semibold text-base leading-tight text-left">
          {show.name}
        </h3>
        <p className="text-sm text-muted-foreground text-left">
          {show.subtitle}
        </p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">👥 {show.groupSize}</span>
          <span>•</span>
          <span className="flex items-center gap-1">⏱️ {show.duration}</span>
        </div>
        {/* Add subtle note when within cut-off period (only if not sold out) */}
        {isWithinCutOff && show.isAvailable !== false && (
          <div className="mt-1">
            <span className="text-xs text-amber-600 dark:text-amber-500 font-medium">
              ⚠️ {t("booking.shows.card.cutOffNote", { count: show.cut_off_days ?? 7 })}
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-shrink-0">
        <Button
          size="sm"
          className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={(e) => {
            e.stopPropagation();
            if (show.isAvailable !== false && !isWithinCutOff) {
              onBookNow();
            }
          }}
          disabled={show.isAvailable === false || isWithinCutOff}
          title={isWithinCutOff ? t("booking.shows.card.cutOffMessage", `Bookings must be made at least ${show.cut_off_days ?? 7} days in advance`) : undefined}
        >
          {show.isAvailable === false
            ? t("booking.shows.card.soldOut")
            : t("booking.shows.card.bookNow")}
        </Button>
        <Button variant="outline" size="sm" onClick={onInfoClick}>
          {variant === "expand" ? (
            isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )
          ) : (
            t("booking.shows.card.info")
          )}
        </Button>
      </div>
    </div>
  );
};

// Mobile card component - standardized to use ShowCardVariant5
const MobileShowCard = ({
  show,
  onShowSelect,
  onBookNow,
  bookingType,
  selectedDate,
}: {
  show: ShowWithAvailability;
  onShowSelect: (show: ShowWithAvailability) => void;
  onBookNow: (show: ShowWithAvailability) => void;
  bookingType?: string;
  selectedDate?: Date;
}) => {
  return (
    <ShowCardVariant5
      show={show}
      onBookNow={() => onBookNow(show)}
      onInfoClick={() => onShowSelect(show)}
      bookingType={bookingType}
      selectedDate={selectedDate}
    />
  );
};

// Sheet Variant Component
const SheetVariant = ({
  show,
  onShowSelect,
  onBookNow,
  t,
  bookingType,
  locale,
}: {
  show: ShowWithAvailability;
  onShowSelect: (show: ShowWithAvailability) => void;
  onBookNow: (show: ShowWithAvailability) => void;
  t: any;
  bookingType?: string;
  locale?: string;
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] cursor-pointer relative ${show.isAvailable === false ? "overflow-hidden" : ""}`}
      onClick={() => onShowSelect(show)}
    >
      {show.isAvailable === false && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 pointer-events-none" />
      )}
      <CardContent className="p-4 relative">
        {!isMobile && (
          <ShowCompactRow
            show={show}
            onBookNow={() => onBookNow(show)}
            onInfoClick={(e) => {
              e.stopPropagation();
              onShowSelect(show);
            }}
            variant="sheet"
            t={t}
            bookingType={bookingType}
            locale={locale}
          />
        )}
      </CardContent>
    </Card>
  );
};

function TimeSlot({
  show,
  onShowSelect,
  onBookNow,
  t,
  bookingType,
  locale,
}: TimeSlotProps & { t: any; bookingType?: string; locale?: string }) {
  // We no longer need the other props like variant, isExpanded, etc.
  return (
    <SheetVariant
      show={show}
      onShowSelect={onShowSelect}
      onBookNow={onBookNow}
      t={t}
      bookingType={bookingType}
      locale={locale}
    />
  );
}

export function ShowsSection({
  initialGroupSize = 6,
  selectedDate,
  excludeBookingId,
  onBack,
  onShowSelect,
  onNavigate,
  venueName,
  supportEmail,
  supportPhone,
}: ShowsSectionProps) {
  const { t, i18n } = useTranslation();
  const searchParams = useSearchParams();
  const router = useRouter();
  const navigate = (url: string, options?: { scroll?: boolean }) => {
    if (onNavigate) {
      onNavigate(url, options);
    } else {
      router.push(url, options);
    }
  };

  // Parse URL parameters - these are the source of truth
  const venueId = searchParams.get("venueId");
  const participants =
    searchParams.get("participants") || initialGroupSize.toString();
  const timeSlotStart = searchParams.get("timeSlotStart") || "10:00";
  const timeSlotEnd = searchParams.get("timeSlotEnd") || "22:00";
  const dayOfWeek = searchParams.get("dayOfWeek");
  const customerTypeId = searchParams.get("customerTypeId");
  const lang =
    (searchParams.get("lang") ||
      i18n.resolvedLanguage ||
      i18n.language ||
      "en")
      .split("-")[0]
      .toLowerCase();
  const dateStr = searchParams.get("date");
  const bookingType = searchParams.get("type") || undefined;

  // Derive values from URL
  const startTime = timeSlotStart;
  const endTime = timeSlotEnd;

  // Memoize date to prevent infinite loops
  const date = useMemo(() => {
    return dateStr ? new Date(dateStr) : selectedDate || new Date();
  }, [dateStr, selectedDate]);

  // Use calendar availability hook - pass all parameters from URL like GroupSection does
  const {
    getDateAvailability,
    isLoading: isLoadingAvailability,
    error: availabilityError,
  } = useCalendarAvailability(
      venueId,
      customerTypeId,
      parseInt(participants) || null, // Pass participants from URL (convert to number)
      timeSlotStart, // Pass time slot start from URL
      timeSlotEnd, // Pass time slot end from URL
    );

  const availabilityErrorRef = useRef<string | null>(null);


  // Use venue details hook
  const {
    minParticipants,
    maxParticipants,
    isLoading: isLoadingVenue,
    languages: venueLanguages,
  } = useVenueDetails(venueId);


  // Ensure groupSize respects venue limits (after venue details are loaded)
  const groupSize = useMemo(() => {
    const urlParticipants = parseInt(participants);
    return Math.max(
      minParticipants,
      Math.min(maxParticipants, urlParticipants),
    );
  }, [participants, minParticipants, maxParticipants]);

  // API state
  const [shows, setShows] = useState<ShowWithAvailability[]>([]);
  const [calendarAvailability, setCalendarAvailability] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [venueDefaultLanguage, setVenueDefaultLanguage] = useState<string | null>(null);

  // Pending filter states for mobile drawer only
  const [pendingGroupSize, setPendingGroupSize] = useState(
    groupSize || minParticipants || 1,
  );
  const [pendingStartTime, setPendingStartTime] = useState(startTime);
  const [pendingEndTime, setPendingEndTime] = useState(endTime);
  const [pendingDate, setPendingDate] = useState<Date | undefined>(date);

  // Other states
  const [selectedShow, setSelectedShow] = useState<Show | undefined>(undefined);
  const [expandedShows, setExpandedShows] = useState<Set<number>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focusSection, setFocusSection] = useState<
    "time" | "date" | "group" | null
  >(null);
  const [showAvailableOnly, setShowAvailableOnly] = useState(false); // Default to showing all shows
  const [showExceedsModal, setShowExceedsModal] = useState(false);
  const [gapWarningOpen, setGapWarningOpen] = useState(false);
  const [isCheckingGap, setIsCheckingGap] = useState(false);
  const [gapWarningData, setGapWarningData] = useState<{
    showName: string;
    date: string;
    startTime: string;
    endTime: string;
    timeGap: number;
  } | null>(null);
  const [participantLimitShow, setParticipantLimitShow] = useState<ShowWithAvailability | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Determine if we should show exceeds modal (based on slider or URL participants exceeding max)
  // Use groupSize (which respects limits) to determine if modal should show
  // The modal shows when attempting to exceed the max
  const shouldShowExceedsModalFromUrl = useMemo(() => {
    if (isLoadingVenue) return false;
    const urlParticipants = parseInt(participants);
    const exceeds = maxParticipants > 0 && urlParticipants > maxParticipants;
    return exceeds;
  }, [participants, maxParticipants, isLoadingVenue]);

  // Helper function to update URL parameters (direct)
  const updateURLParam = (key: string, value: string) => {
    // Only update if value actually changed
    if (searchParams.get(key) === value) return;

    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    navigate(`/bookings/shows?${params.toString()}`, { scroll: false });
  };

  const normalizeTimeRange = (start: string, end: string) => {
    if (timeToMinutes(end) < timeToMinutes(start)) {
      return { start, end: start };
    }
    return { start, end };
  };

  const updateTimeRange = (nextStart: string, nextEnd: string) => {
    const normalized = normalizeTimeRange(nextStart, nextEnd);

    if (normalized.start === startTime && normalized.end === endTime) {
      return;
    }

    const params = new URLSearchParams(searchParams);
    params.set("timeSlotStart", normalized.start);
    params.set("timeSlotEnd", normalized.end);
    navigate(`/bookings/shows?${params.toString()}`, { scroll: false });
  };

  const handlePendingStartTimeChange = (time: string) => {
    const normalized = normalizeTimeRange(time, pendingEndTime);
    setPendingStartTime(normalized.start);
    setPendingEndTime(normalized.end);
  };

  const handlePendingEndTimeChange = (time: string) => {
    const normalized = normalizeTimeRange(pendingStartTime, time);
    setPendingStartTime(normalized.start);
    setPendingEndTime(normalized.end);
  };

  useEffect(() => {
    if (!availabilityError) {
      availabilityErrorRef.current = null;
      return;
    }

    if (availabilityError === "timeSlotStart must be before timeSlotEnd") {
      if (availabilityErrorRef.current !== availabilityError) {
        toast.error(t("booking.shows.filters.invalidTimeRange"));
        availabilityErrorRef.current = availabilityError;
      }

      const normalized = normalizeTimeRange(startTime, endTime);
      if (normalized.start !== startTime || normalized.end !== endTime) {
        updateTimeRange(startTime, endTime);
      }
    }
  }, [availabilityError, startTime, endTime, t]);

  // Debounced effect to sync pendingGroupSize to URL (for desktop slider smoothness)
  // BUT don't update if it exceeds maxParticipants (let modal handle that)
  // Sync i18n language with URL lang parameter
  useEffect(() => {
    const urlLang = searchParams.get("lang");
    if (urlLang && urlLang !== i18n.language) {
      i18n.changeLanguage(urlLang);
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    // Only debounce if pendingGroupSize is different from URL groupSize AND doesn't exceed max
    if (pendingGroupSize !== groupSize && pendingGroupSize <= maxParticipants) {
      const timeout = setTimeout(() => {
        updateURLParam("participants", pendingGroupSize.toString());
      }, 250); // 250ms delay after user stops dragging

      return () => clearTimeout(timeout);
    }
  }, [pendingGroupSize]); // Only re-run when pendingGroupSize changes

  // Helper function to update date and dayOfWeek together
  const updateDateAndDayOfWeek = (newDate: Date) => {
    // Format date as YYYY-MM-DD in local timezone
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, "0");
    const day = String(newDate.getDate()).padStart(2, "0");
    const newDateStr = `${year}-${month}-${day}`;

    const newDayOfWeek = newDate.getDay().toString(); // 0-6 (Sunday-Saturday)

    // Only update if date actually changed
    if (dateStr === newDateStr) return;

    const params = new URLSearchParams(searchParams);
    params.set("date", newDateStr);
    params.set("dayOfWeek", newDayOfWeek);
    navigate(`/bookings/shows?${params.toString()}`, { scroll: false });
  };

  // Fetch shows from API using URL parameters
  useEffect(() => {
    // Create AbortController for request cancellation
    const controller = new AbortController();

    const fetchShows = async () => {
      // Skip until required params are available; keep previous loading state so UI still shows skeleton.
      if (!venueId || !dayOfWeek || !customerTypeId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Format date for API (YYYY-MM-DD) in local timezone
        const formatLocalDate = (d: Date) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };
        const formattedDate = date
          ? formatLocalDate(date)
          : formatLocalDate(new Date());

        const params = new URLSearchParams({
          venue_id: venueId,
          participants: groupSize.toString(), // Use clamped groupSize instead of raw participants
          timeslot_start: startTime,
          timeslot_end: endTime,
          day_of_week: dayOfWeek,
          customer_type_id: customerTypeId,
          lang,
          date: formattedDate,
        });

        const response = await fetch(`/api/bookings/shows?${params}`, {
          signal: controller.signal, // Add abort signal
        });

        // Check if request was aborted
        if (controller.signal.aborted) {
          return;
        }

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch shows");
        }

        if (data.success) {
          if (data.data && data.data.length > 0) {

            // Extract venue default language from first show (all shows have the same venue)
            const firstShow = data.data[0] as ApiShow;
            if (firstShow.venueDefaultLanguage) {
              setVenueDefaultLanguage(firstShow.venueDefaultLanguage);
            }

            const convertedShows = data.data.map((show: ApiShow) =>
              convertApiShowToShow(show, bookingType),
            );

            // Now fetch calendar availability to check individual slot availability

            try {
              const calendarParams = new URLSearchParams({
                venue_id: venueId,
                participants: groupSize.toString(),
                timeSlotStart: startTime,
                timeSlotEnd: endTime,
                customer_type_id: customerTypeId,
                start_date: formattedDate,
                end_date: formattedDate, // Only check for the selected date
              });

              const calendarResponse = await fetch(
                `/api/bookings/calendar-availability?${calendarParams}`,
                {
                  signal: controller.signal,
                }
              );

              if (calendarResponse.ok) {
                const calendarData = await calendarResponse.json();

                if (calendarData.success && calendarData.data && calendarData.data.length > 0) {
                  const dayAvailability = calendarData.data[0]; // First day's data
                  setCalendarAvailability(dayAvailability);

                  // Check each show's time slot availability including duration overlaps
                  const showsWithAvailability = await Promise.all(
                    convertedShows.map(async (show: ShowWithAvailability) => {
                      // Find the slot availability for this specific show and time
                      const slotInfo = dayAvailability.slots?.find(
                        (slot: any) => slot.time_slot === show.startTime + ":00" && slot.show_id === show.id
                      );

                      // Combine availability from both API sources:
                      // 1. show.isAvailable from shows API (studio booking status)
                      // 2. slotInfo.available from calendar API (includes studio availability check)
                      // The calendar-availability API now includes studio availability in its response
                      let calendarAvailable = slotInfo ? slotInfo.available : true;

                      // In edit mode, we need special handling
                      if (excludeBookingId) {
                        // For edit mode, check if there are available studios
                        // The calendar API already provides available_studio_ids
                        if (slotInfo && slotInfo.available_studio_ids) {
                          calendarAvailable = slotInfo.available_studio_ids.length > 0;
                        } else {
                          // If no slot info, assume available (backward compatibility)
                          calendarAvailable = true;
                        }
                      }

                      const isAvailable = (show.isAvailable !== false) && calendarAvailable;

                      return {
                        ...show,
                        isAvailable,
                      };
                    })
                  );

                  setShows(showsWithAvailability);
                } else {
                  // No calendar availability data, assume all shows are available
                  const showsWithAvailability = convertedShows.map(
                    (show: ShowWithAvailability) => ({
                      ...show,
                      isAvailable: true,
                    }),
                  );
                  setShows(showsWithAvailability);
                }
              } else {
                // Calendar API failed, fallback to marking all as available
                console.error("❌ Calendar availability API failed");
                const showsWithAvailability = convertedShows.map(
                  (show: ShowWithAvailability) => ({
                    ...show,
                    isAvailable: true,
                  }),
                );
                setShows(showsWithAvailability);
              }
            } catch (calendarError) {
              console.error("❌ Error fetching calendar availability:", calendarError);
              // Fallback: mark all shows as available
              const showsWithAvailability = convertedShows.map(
                (show: ShowWithAvailability) => ({
                  ...show,
                  isAvailable: true,
                }),
              );
              setShows(showsWithAvailability);
            }
          } else {
            // API returned empty result - NO SHOWS AVAILABLE
            setShows([]);
          }
        } else {
          throw new Error(data.error || "Failed to fetch shows");
        }
      } catch (err) {
        console.error("❌ Error fetching shows:", err);
        setError(err instanceof Error ? err.message : "Failed to load shows");
        // NO MOCK DATA FALLBACK - show empty state instead
        setShows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchShows();

    // Cleanup: abort fetch if component unmounts or deps change
    return () => {
      controller.abort();
    };
  }, [
    venueId,
    participants, // Use URL param directly to avoid dependency loop with groupSize
    startTime,
    endTime,
    dayOfWeek,
    customerTypeId,
    lang,
    dateStr,
  ]); // dayOfWeek now properly updates with date

  // Sync pending states when URL changes (use dateStr instead of date object)
  useEffect(() => {
    setPendingDate(date);
    setPendingGroupSize((prev) => {
      const newSize = groupSize || minParticipants || 1;
      return prev !== newSize ? newSize : prev;
    });
    setPendingStartTime((prev) => (prev !== startTime ? startTime : prev));
    setPendingEndTime((prev) => (prev !== endTime ? endTime : prev));
  }, [dateStr, groupSize, startTime, endTime, date, minParticipants]);

  // Initialize pending states when drawer opens
  const handleDrawerOpen = (section?: "time" | "date" | "group") => {
    setPendingDate(date);
    setPendingGroupSize(groupSize || minParticipants || 1);
    setPendingStartTime(startTime);
    setPendingEndTime(endTime);
    setFocusSection(section || null);
    setDrawerOpen(true);
  };

  // Apply pending filters by updating URL
  const handleApplyFilters = (overrideDate?: Date) => {
    const params = new URLSearchParams(searchParams);
    params.set("participants", pendingGroupSize.toString());

    const normalized = normalizeTimeRange(pendingStartTime, pendingEndTime);
    params.set("timeSlotStart", normalized.start);
    params.set("timeSlotEnd", normalized.end);
    if (normalized.end !== pendingEndTime) {
      setPendingEndTime(normalized.end);
    }
    if (normalized.start !== pendingStartTime) {
      setPendingStartTime(normalized.start);
    }

    const dateToUse =
      overrideDate instanceof Date ? overrideDate : pendingDate;
    if (dateToUse instanceof Date && !Number.isNaN(dateToUse.getTime())) {
      // Format date as YYYY-MM-DD in local timezone
      const year = dateToUse.getFullYear();
      const month = String(dateToUse.getMonth() + 1).padStart(2, "0");
      const day = String(dateToUse.getDate()).padStart(2, "0");
      params.set("date", `${year}-${month}-${day}`);
      params.set("dayOfWeek", dateToUse.getDay().toString()); // 0-6 (Sunday-Saturday)
    }

    navigate(`/bookings/shows?${params.toString()}`, { scroll: false });
    setDrawerOpen(false);
  };

  // Reset pending filters to current URL values
  const handleResetFilters = () => {
    setPendingDate(date);
    setPendingGroupSize(groupSize || minParticipants || 1);
    setPendingStartTime(startTime);
    setPendingEndTime(endTime);
  };

  // Clear focus section after a delay
  useEffect(() => {
    if (focusSection) {
      const timer = setTimeout(() => {
        setFocusSection(null);
      }, 1500); // Clear after 1.5 seconds
      return () => clearTimeout(timer);
    }
  }, [focusSection]);

  // Handle drawer close without applying
  const handleDrawerClose = () => {
    // Reset pending states to URL values
    setPendingDate(date);
    setPendingGroupSize(groupSize || minParticipants || 1);
    setPendingStartTime(startTime);
    setPendingEndTime(endTime);
    setDrawerOpen(false);
    setFocusSection(null);
  };

  const handleToggleExpand = (showId: number) => {
    setExpandedShows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(showId)) {
        newSet.delete(showId);
      } else {
        newSet.add(showId);
      }
      return newSet;
    });
  };

  const checkBookingGap = async (show: ShowWithAvailability, userEmail?: string) => {
    try {
      setIsCheckingGap(true);

      if (!date) {
        toast.error(t("booking.errors.dateRequired", "Please select a date"));
        setIsCheckingGap(false);
        return;
      }

      // Gap check is disabled for customer portal - proceed directly with booking
      onShowSelect(show, date);
    } catch (error) {
      console.error("Error in booking flow:", error);
      // On error, allow the booking to proceed
      onShowSelect(show, date);
    } finally {
      setIsCheckingGap(false);
    }
  };

  const handleGapWarningCancel = () => {
    setGapWarningOpen(false);
    setGapWarningData(null);
  };

  const showFitsParticipants = (show: ShowWithAvailability) => {
    const requested = parseInt(participants);
    if (Number.isNaN(requested)) return true;
    const showMax = show.max_participants;
    const showMin = show.min_participants;
    if (typeof showMax === "number" && requested > showMax) return false;
    if (typeof showMin === "number" && requested < showMin) return false;
    return true;
  };

  const handleBookNow = () => {
    if (!selectedShow || !date) return;
    if (!showFitsParticipants(selectedShow)) {
      setParticipantLimitShow(selectedShow);
      return;
    }
    checkBookingGap(selectedShow);
  };

  const handleDirectBookNow = (show: ShowWithAvailability) => {
    if (!date) return;
    if (!showFitsParticipants(show)) {
      setParticipantLimitShow(show);
      return;
    }
    checkBookingGap(show);
  };

  const handleParticipantLimitAdjust = () => {
    if (!participantLimitShow) return;
    const requested = parseInt(participants);
    const showMax = participantLimitShow.max_participants;
    const showMin = participantLimitShow.min_participants;
    let adjusted = requested;
    if (typeof showMax === "number" && requested > showMax) {
      adjusted = showMax;
    } else if (typeof showMin === "number" && requested < showMin) {
      adjusted = showMin;
    }
    setParticipantLimitShow(null);
    setPendingGroupSize(adjusted);
    updateURLParam("participants", adjusted.toString());
  };

  const handleParticipantLimitCancel = () => {
    setParticipantLimitShow(null);
  };

  const isDateAvailable = (date: Date) => {
    const availability = getDateAvailability(date);
    return availability === "available" || availability === "partial";
  };

  // Filter shows based on time range and availability
  const getFilteredShows = () => {
    return shows
      .filter((show) => {
        // Filter by availability if checkbox is checked
        if (showAvailableOnly && !show.isAvailable) {
          return false;
        }

        // Filter by time range
        const showTime = show.startTime;
        const start = startTime;
        const end = endTime;

        const showMinutes = timeToMinutes(showTime);
        const startMinutes = timeToMinutes(start);
        const endMinutes = timeToMinutes(end);

        return showMinutes >= startMinutes && showMinutes <= endMinutes;
      })
      .sort((a, b) => {
        // Sort chronologically by start time
        const timeA = new Date(`2000-01-01 ${a.startTime}`).getTime();
        const timeB = new Date(`2000-01-01 ${b.startTime}`).getTime();
        return timeA - timeB;
      });
  };

  const filteredShows = getFilteredShows();

  return (
    <section className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm hover:text-primary bg-transparent border-none p-0 outline-none focus:outline-none flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("booking.shows.back")}
              </span>
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img
                src="https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/on-air-logo.png"
                alt="On Air Game Shows"
                className="h-8 sm:h-9 md:h-10 w-auto p-2 rounded-lg shadow-md"
                style={{
                  backgroundImage:
                    "url(https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/footer-background-sm.jpg)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                <span className="hidden sm:inline">
                  {t("booking.shows.step", { current: 4, total: 5 })}
                </span>
                <span className="sm:hidden text-xs">4/5</span>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <div className="w-2 h-2 rounded-full bg-muted"></div>
                </div>
              </div>
              <LanguageSelector availableLanguages={venueLanguages} />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="text-center mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold">
            {t("booking.shows.title")}
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Mobile Filter Bar - RESTORED ORIGINAL */}
          {isMobile && (
            <div className="lg:hidden p-1 border-2 border-border rounded-lg">
              <FilterBar
                groupSize={groupSize}
                date={date}
                startTime={startTime}
                endTime={endTime}
                onClick={handleDrawerOpen}
              />
            </div>
          )}

          {/* Desktop Sidebar / Mobile Drawer - RESTORED ORIGINAL */}
          {isMobile ? (
            <FilterDrawer
              open={drawerOpen}
              onClose={handleDrawerClose}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            >
              <ShowFilters
                selectedShow={selectedShow || undefined}
                pendingDate={pendingDate}
                setPendingDate={setPendingDate}
                pendingGroupSize={pendingGroupSize}
                setPendingGroupSize={(size) => {
                  if (size > maxParticipants) {
                    setPendingGroupSize(maxParticipants);
                    setShowExceedsModal(true);
                  } else {
                    setPendingGroupSize(size);
                  }
                }}
                pendingStartTime={pendingStartTime}
                setPendingStartTime={handlePendingStartTimeChange}
                pendingEndTime={pendingEndTime}
                setPendingEndTime={handlePendingEndTimeChange}
                getDateAvailability={getDateAvailability}
                isDateAvailable={isDateAvailable}
                isLoadingAvailability={isLoadingAvailability}
                showApplyButtons={false}
                minParticipants={minParticipants}
                maxParticipants={maxParticipants}
                focusSection={focusSection}
                onDateClick={handleApplyFilters}
                isMobile={true}
                onClose={handleDrawerClose}
              />
            </FilterDrawer>
          ) : (
            <div className="hidden lg:block lg:w-80 space-y-6">
              <div className="bg-card rounded-lg border sticky top-[84px]">
                <ShowFilters
                  selectedShow={selectedShow || undefined}
                  pendingDate={date}
                  setPendingDate={(newDate) => {
                    // Update URL immediately on desktop with dayOfWeek
                    if (newDate) {
                      updateDateAndDayOfWeek(newDate);
                    }
                  }}
                  pendingGroupSize={pendingGroupSize}
                  setPendingGroupSize={(size) => {
                    if (size > maxParticipants) {
                      setPendingGroupSize(maxParticipants);
                      setShowExceedsModal(true);
                    } else {
                      setPendingGroupSize(size);
                    }
                  }}
                  getDateAvailability={getDateAvailability}
                  isDateAvailable={isDateAvailable}
                  isLoadingAvailability={isLoadingAvailability}
                  showApplyButtons={false} // No apply buttons on desktop
                  minParticipants={minParticipants}
                  maxParticipants={maxParticipants}
                  focusSection={null} // No focus highlighting on desktop
                  isMobile={false}
                />
              </div>
            </div>
          )}

          <div className="flex-1">
            {/* Availability Filter and Time Range */}
            <div className="mb-4">
              <div className="p-3 bg-card rounded-lg border shadow-sm space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="available-only"
                      checked={showAvailableOnly}
                      onChange={(e) => setShowAvailableOnly(e.target.checked)} // Toggle state but don't filter
                      className="h-4 w-4 rounded border-gray-300 accent-purple-900 focus:ring-purple-900 focus:ring-2 cursor-pointer"
                    />
                    <Label
                      htmlFor="available-only"
                      className="text-sm font-medium cursor-pointer"
                    >
                      {t("booking.shows.filters.availableOnly")}
                    </Label>
                  </div>
                  <div className="hidden sm:block">
                    <TimeRangeSelector
                      startTime={startTime}
                      endTime={endTime}
                      onStartTimeChange={(time) =>
                        updateTimeRange(time, endTime)
                      }
                      onEndTimeChange={(time) =>
                        updateTimeRange(startTime, time)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Shows Display - RESTORED ORIGINAL with API integration */}
              {loading ? (
                <div className="mt-2 space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-28 space-y-2">
                            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="h-5 bg-gray-200 rounded animate-pulse w-1/3"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
                          </div>
                          <div className="flex gap-2">
                            <div className="h-9 w-20 bg-gray-200 rounded animate-pulse"></div>
                            <div className="h-9 w-16 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredShows.length > 0 ? (
                <div className="mt-2">
                  {isMobile ? (
                    // Mobile view with one card per row
                    <>
                      {/* Pricing info text for mobile only - aligned to left */}
                      <div className="mb-3 px-2">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">ℹ️</span>
                          <p className="text-sm text-muted-foreground text-left">
                            {bookingType === "company"
                              ? t("booking.shows.pricingInfo.corporate")
                              : t("booking.shows.pricingInfo.private")}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-4">
                        {filteredShows.map((show) => (
                        <MobileShowCard
                          key={show.show_time_slot_pricing_id || show.id}
                          show={show}
                          onShowSelect={(show) => {
                            setSelectedShow(show);
                          }}
                          onBookNow={handleDirectBookNow}
                          bookingType={bookingType}
                          selectedDate={date}
                        />
                      ))}
                      </div>
                    </>
                  ) : (
                    // Desktop view - keep original
                    <>
                      {/* Pricing info text for desktop - aligned to left */}
                      <div className="mb-3 px-2">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">ℹ️</span>
                          <p className="text-sm text-muted-foreground text-left">
                            {bookingType === "company"
                              ? t("booking.shows.pricingInfo.corporate")
                              : t("booking.shows.pricingInfo.private")}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                      {filteredShows.map((show) => {
                        return (
                          <TimeSlot
                            key={show.show_time_slot_pricing_id || show.id}
                            show={show}
                            variant="sheet"
                            onShowSelect={(show) => {
                              setSelectedShow(show);
                            }}
                            onBookNow={handleDirectBookNow}
                            isExpanded={expandedShows.has(show.id)}
                            onToggleExpand={() => handleToggleExpand(show.id)}
                            t={t}
                            bookingType={bookingType || undefined}
                            locale={i18n.language || venueDefaultLanguage || "en"}
                          />
                        );
                      })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-lg font-medium text-muted-foreground">
                    {t("booking.shows.empty.noShows")}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {error
                      ? t("booking.shows.empty.tryAgain")
                      : t("booking.shows.empty.adjustFilters")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Render the appropriate variant based on device type - RESTORED ORIGINAL */}
      {selectedShow &&
        (isMobile ? (
          <SheetVariantFour
            show={selectedShow as any}
            date={date!}
            open={true}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedShow(undefined);
              }
            }}
            onBookNow={handleBookNow}
            bookingType={bookingType}
          />
        ) : (
          <ModalVariantThree
            show={selectedShow as any}
            date={date!}
            open={true}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedShow(undefined);
              }
            }}
            onBookNow={handleBookNow}
            bookingType={bookingType}
          />
        ))}

      {/* Modal Overlay - Shows on top of page */}
      {(shouldShowExceedsModalFromUrl || showExceedsModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 my-8">
            <div className="max-h-[calc(100vh-64px)] overflow-y-auto">
              <GroupSizeExceedsModal
                maxGroupSize={maxParticipants}
                venueName={venueName}
                supportEmail={supportEmail || undefined}
                supportPhone={supportPhone || undefined}
                isOverlay={true}
                onBack={() => {
                  setShowExceedsModal(false);
                  // Navigate back to group selection if URL had exceeds
                  if (shouldShowExceedsModalFromUrl) {
                    const lang = searchParams.get("lang") || "en";
                    const type = searchParams.get("type") || "private";
                    router.push(`/bookings/group?lang=${lang}&type=${type}&venueId=${venueId}`);
                  } else {
                    updateURLParam("participants", maxParticipants.toString());
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Booking Gap Warning Modal */}
      <BookingGapWarningModal
        isOpen={gapWarningOpen}
        onCancel={handleGapWarningCancel}
        conflictingShow={gapWarningData || undefined}
      />

      {/* Show participant limit modal (when group size doesn't fit the selected show) */}
      <ShowMaxParticipantsModal
        isOpen={participantLimitShow !== null}
        onCancel={handleParticipantLimitCancel}
        onAdjust={handleParticipantLimitAdjust}
        currentParticipants={parseInt(participants) || 0}
        showMaxParticipants={participantLimitShow?.max_participants ?? 0}
        showMinParticipants={participantLimitShow?.min_participants ?? 1}
        showName={participantLimitShow?.name}
      />
    </section>
  );
}
